import type { EnemyForce, EnemyState } from '@/combat/enemies';
import { swingArc } from '@/combat/swing';
import type { Facing } from '@/sprites/katie';
import type { NpcId } from '@/data/npcs';
import { TILE_SIZE } from '@/sprites/tiles';

/**
 * Someone walking with you.
 *
 * Temporary on purpose. A permanent companion is a second character to
 * balance around and quietly makes every fight easier forever; a friend who
 * comes out for an afternoon is a favour, and favours are what friendship is
 * for. She goes home at dusk whatever is happening, because she said she
 * would.
 *
 * Not saved, for the same reason enemies are not: she is a thing that is
 * happening rather than a thing that exists. Reloading returns you to the
 * world as it stands, and as it stands nobody is following you.
 */

/** How close she tries to stay, in pixels. Roughly a tile and a half. */
const FOLLOW_DISTANCE = 24;
/** Beyond this she stops sightseeing and catches up. */
const CATCH_UP_DISTANCE = 96;
const SPEED = 62;

/** She swings at whatever gets close, on her own rhythm. */
export const HELPER_DAMAGE = 7;
export const HELPER_REACH = 1.4;
export const HELPER_COOLDOWN = 0.75;
/**
 * The hour she goes home: seven, the same hour her schedule already sends her
 * back, so "she works until seven" stays one fact about her.
 */
export const HELPER_HOME_HOUR = 19;

const MINUTES_PER_DAY = 24 * 60;

/**
 * Seven o'clock this evening, as an absolute minute.
 *
 * Absolute rather than a time of day: a deadline of "1140 minutes" compared
 * against a clock that wraps at midnight would be met by sleeping, and she
 * would follow you around forever having been asked once.
 */
export function homeTimeFor(totalMinutes: number): number {
  return Math.floor(totalMinutes / MINUTES_PER_DAY) * MINUTES_PER_DAY + HELPER_HOME_HOUR * 60;
}

/** How far she will look for something to hit, in pixels. */
export const HELPER_AGGRO = 64;

export interface HelperContext {
  readonly playerX: number;
  readonly playerY: number;
  readonly enemies: EnemyForce;
  /** The clock's absolute minute, so she goes home when she said she would. */
  readonly minutes: number;
}

export interface HelperStrike {
  readonly killed: EnemyState[];
  readonly hits: number;
}

export class Helper {
  private present = false;
  private who: NpcId = 'mira';
  private untilMinutes = 0;
  private cooldown = 0;

  x = 0;
  y = 0;
  facing: Facing = 'down';

  get isPresent(): boolean {
    return this.present;
  }

  get id(): NpcId {
    return this.who;
  }

  /** Ask someone along until a given time of day. */
  summon(who: NpcId, x: number, y: number, untilMinutes: number): void {
    this.present = true;
    this.who = who;
    this.x = x;
    this.y = y;
    this.untilMinutes = untilMinutes;
    this.cooldown = 0;
  }

  dismiss(): void {
    this.present = false;
  }

  /**
   * Walk, and swing at what is near.
   *
   * Returns what she killed so the caller can drop spoils exactly the way it
   * does for the player's own kills — a helper whose kills give nothing would
   * make bringing her along a cost.
   */
  update(dt: number, context: HelperContext): HelperStrike {
    const empty: HelperStrike = { killed: [], hits: 0 };
    if (!this.present) return empty;

    // Dusk sends her home mid-step, mid-fight, mid-anything.
    if (context.minutes >= this.untilMinutes) {
      this.present = false;
      return empty;
    }

    this.walk(dt, context);

    this.cooldown = Math.max(0, this.cooldown - dt);
    const target = this.nearestEnemy(context.enemies);
    if (!target || this.cooldown > 0) return empty;

    this.facing = facingToward(target.x - this.x, target.y - this.y);
    this.cooldown = HELPER_COOLDOWN;

    const results = context.enemies.resolveHit(
      swingArc(this.x, this.y, this.facing, HELPER_REACH),
      HELPER_DAMAGE,
      this.x,
      this.y,
    );

    return {
      killed: results.filter((result) => result.killed).map((result) => result.state),
      hits: results.length,
    };
  }

  /**
   * Trail the player.
   *
   * Deliberately not pathfinding: she walks straight at you and gives up on
   * standing exactly anywhere. Walls are a problem she solves by you walking
   * back out of the room, which is what following someone actually looks like.
   */
  private walk(dt: number, context: HelperContext): void {
    const dx = context.playerX - this.x;
    const dy = context.playerY - this.y;
    const distance = Math.hypot(dx, dy);
    if (distance <= FOLLOW_DISTANCE) return;

    // A long way behind — a door, a fight, a bad corner — and she cuts the
    // distance faster rather than being lost for the afternoon.
    const speed = distance > CATCH_UP_DISTANCE ? SPEED * 2 : SPEED;
    const step = Math.min(distance - FOLLOW_DISTANCE, speed * dt);

    this.x += (dx / distance) * step;
    this.y += (dy / distance) * step;
    this.facing = facingToward(dx, dy);
  }

  private nearestEnemy(enemies: EnemyForce): EnemyState | null {
    let best: EnemyState | null = null;
    let bestDistance = HELPER_AGGRO;

    for (const [, state] of enemies.states.entries()) {
      const distance = Math.hypot(state.x - this.x, state.y - this.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = state;
      }
    }

    return best;
  }
}

/** Which way something lies, snapped to the four facings the art has. */
function facingToward(dx: number, dy: number): Facing {
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'down' : 'up';
}

/** Where she should stand when she joins you: just behind your shoulder. */
export function summonPosition(playerX: number, playerY: number): { x: number; y: number } {
  return { x: playerX - TILE_SIZE, y: playerY };
}
